// src/views/admin/invoices/index.jsx
import { useToast, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import InvoicesTable from '../../../components/Invoices/InvoiceTable.js';
import InvoiceFilters from '../../../components/Invoices/InvoiceFilters.js';
import SyncProgressModal from '../../../components/Invoices/SyncProgressModal.js';
import InvoicePdfPreviewModal from '../../../components/Invoices/InvoicePdfPreviewModal.js';
import {
  buildInvoicePdfFilename,
  buildInvoicePdfHints,
  downloadPdfBlob,
  fetchInvoicePdfBlob,
  openPdfBlobInNewTab,
  resolveInvoiceDocumentId,
} from '../../../utils/invoiceDocument.js';
import { columnsDataInvoices } from './variables/columnsDataInvoices.js';
import React, { useEffect, useState, useRef } from 'react';
import { getData, postData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import { loaderService } from '../../../services/loaderService.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';

export default function Invoices() {
  const { resetListFilters } = useSearch(); 
  const [data, setData] = useState([]); 
  const [page, setPage] = useState(1); // Cambiar a 1-based para el backend
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0); 
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false); // Separar estado de sincronización
  const [currentFilters, setCurrentFilters] = useState({});
  
  // Estados para el modal de progreso
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    isRunning: false,
    current: 0,
    total: 0,
    phase: '',
    message: '',
    percentage: 0
  });
  
  const rowsPerPage = 10; 
  const toast = useToast();
  const navigate = useNavigate();
  const syncPollingRef = useRef(null);
  const skipSortReloadRef = useRef(true);
  const pdfBlobRef = useRef(null);
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();
  const [pdfPreview, setPdfPreview] = useState({
    isOpen: false,
    blobUrl: null,
    title: '',
    filename: 'factura.pdf',
  });

  // Función para obtener el progreso de sincronización
  const checkSyncProgress = async () => {
    try {
      console.log('🔍 Checking sync progress...');
      const response = await getData('/invoices/sync/progress');
      const progress = response.payload;
      
      console.log('📊 Sync progress response:', progress);
      
      setSyncProgress(progress);
      setIsSyncing(progress.isRunning);
      
      // Si la sincronización está corriendo y no se muestra el modal, mostrarlo
      if (progress.isRunning && !showSyncModal) {
        console.log('🎬 Starting to show sync modal');
        setShowSyncModal(true);
      }
      
      // Si la sincronización terminó
      if (!progress.isRunning && showSyncModal) {
        console.log('✅ Sync completed, will close modal in 2 seconds');
        // Esperar un poco para que el usuario vea el mensaje de completado
        setTimeout(() => {
          console.log('🔒 Closing sync modal and reloading data');
          setShowSyncModal(false);
          // Solo recargar datos si no estamos ya cargando y si no hay error
          if (!isLoading && progress.phase !== 'error') {
            loadData(currentFilters, page, true);
          }
        }, progress.phase === 'error' ? 3000 : 2000); // Más tiempo para errores
      }
      
      return progress;
    } catch (error) {
      console.error('❌ Error checking sync progress:', error);
      return null;
    }
  };

  // Iniciar polling del progreso
  const startSyncProgressPolling = () => {
    console.log('🔄 Starting sync progress polling...');
    if (syncPollingRef.current) {
      console.log('⏹️ Clearing existing polling interval');
      clearInterval(syncPollingRef.current);
    }
    
    syncPollingRef.current = setInterval(async () => {
      console.log('⏰ Polling sync progress...');
      const progress = await checkSyncProgress();
      
      // Si no hay sincronización corriendo, detener el polling
      if (progress && !progress.isRunning) {
        console.log('🛑 Sync not running, stopping polling');
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
        setIsSyncing(false);
      }
    }, 1500); // Verificar cada 1.5 segundos para ver mejor las transiciones
  };

  // Detener polling del progreso
  const stopSyncProgressPolling = () => {
    console.log('🛑 Stopping sync progress polling...');
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }
    setIsSyncing(false);
  };

  const loadData = async (filters = {}, pageNum = 1, skipSync = false) => {
    try {
      console.log('🚀 LoadData called with:', { filters, pageNum, skipSync, isLoading, isSyncing });
      
      // Solo mostrar loader si no hay sincronización en curso
      if (!isSyncing && !showSyncModal) {
        console.log('📊 Setting isLoading to true');
        setIsLoading(true);
        // Desactivar loader global durante sync
        loaderService.hideLoader();
      } else {
        console.log('🔄 Skipping loader because sync is running or modal is shown');
        setIsLoading(false);
        // Asegurar que el loader global esté desactivado
        loaderService.hideLoader();
      }
      
      console.log('Loading data with filters:', filters, 'page:', pageNum);
      
      // Verificar si hay una sincronización en curso ANTES de cualquier otra acción
      if (!skipSync) {
        try {
          console.log('� Checking for background sync or starting new one...');
          
          // Verificar si ya hay una sincronización en curso
          console.log('🔍 Checking if sync is already in progress...');
          const currentProgress = await checkSyncProgress();
          if (currentProgress && currentProgress.isRunning) {
            console.log('⚠️ Found background sync in progress, resuming UI...');
            setSyncProgress(currentProgress);
            setIsSyncing(true);
            setShowSyncModal(true);
            startSyncProgressPolling();
            setIsLoading(false); // Quitar loader normal si hay sincronización
            return; // Salir y esperar a que termine la sincronización
          }
          
          // Iniciar sincronización solo si no hay una en curso
          console.log('🚀 Starting new sync...');
          setIsSyncing(true);
          const syncPromise = postData('/invoices/sync/smart', {});
          
          // Iniciar polling del progreso inmediatamente
          console.log('⏰ Starting progress polling...');
          startSyncProgressPolling();
          
          console.log('⏳ Waiting for sync response...');
          const syncResponse = await syncPromise;
          console.log('📨 Sync response received:', syncResponse);
          
          if (syncResponse.status === 200) {
            const { synced, skipped, isFirstSync } = syncResponse.payload;
            console.log(`✅ Auto-sync completed: ${synced} synced, ${skipped} skipped, first sync: ${isFirstSync}`);
            
            // Solo mostrar notificación toast si no se mostró el modal (para sincronizaciones pequeñas)
            if (!showSyncModal && (isFirstSync || synced > 0)) {
              toast({
                title: isFirstSync ? 'Sincronización inicial completada' : 'Nuevas facturas sincronizadas',
                description: `${synced} facturas sincronizadas${skipped > 0 ? `, ${skipped} ya existían` : ''}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          }
        } catch (syncError) {
          console.error('❌ Auto-sync failed, continuing with local data:', syncError);
          stopSyncProgressPolling();
          setIsSyncing(false);
          // No mostrar error de sincronización al usuario, continuar con datos locales
        }
      }
      
      // SIEMPRE usar el endpoint de búsqueda local para mejor rendimiento
      console.log('🔍 Using local search endpoint');
      const queryParams = new URLSearchParams({
        page: pageNum.toString(),
        limit: rowsPerPage.toString(),
        ...filters
      });
      appendSortParams(queryParams);

      console.log('📞 Calling local search with params:', queryParams.toString());
      const response = await getData(`/invoices/search/local?${queryParams}`);
      console.log('📄 Local search response:', response);
      
      const result = response?.payload;
      
      console.log('📊 Setting data:', {
        invoices: result?.invoices?.length || 0,
        total: result?.total || 0,
        totalPages: result?.totalPages || 0
      });
      
      setData(result?.invoices || []);
      setTotal(result?.total || 0);
      setTotalPages(result?.totalPages || 0);
      
    } catch (error) {
      console.error('❌ Error loading data:', error);
      stopSyncProgressPolling();
      setIsSyncing(false);
      toast({
        title: 'Error al obtener facturas',
        description: error.message || 'No se pudieron cargar las facturas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      // Solo quitar loader si no hay sincronización en curso
      if (!isSyncing && !showSyncModal) {
        console.log('📊 Setting isLoading to false');
        setIsLoading(false);
      } else {
        console.log('🔄 Keeping loader off because sync is running or modal is shown');
      }
    }
  };

  const handleSearch = (filters) => {
    setCurrentFilters(filters);
    setPage(1); // Resetear a la primera página
    // Saltar sync en búsquedas porque ya se sincronizó al cargar
    // Solo buscar si no hay sincronización en curso
    if (!isSyncing) {
      loadData(filters, 1, true);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Saltar sync en cambios de página
    // Solo cambiar página si no hay sincronización en curso
    if (!isSyncing) {
      loadData(currentFilters, newPage, true);
    }
  };

  const handleManualSync = async () => {
    if (isSyncing) return;

    try {
      setIsSyncing(true);
      setShowSyncModal(true);
      startSyncProgressPolling();
      await postData('/invoices/sync/smart', {});
    } catch (error) {
      stopSyncProgressPolling();
      setIsSyncing(false);
      toast({
        title: 'Error al sincronizar',
        description: error.message || 'No se pudieron sincronizar las facturas.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const closePdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return {
        isOpen: false,
        blobUrl: null,
        title: '',
        filename: 'factura.pdf',
      };
    });
    pdfBlobRef.current = null;
  };

  const handlePrintInvoice = async (action, rowData) => {
    const docId = resolveInvoiceDocumentId(rowData);
    if (!docId) {
      toast({
        title: 'No se puede abrir el PDF',
        description: 'La factura no tiene ID de comprobante en el sistema.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      const blob = await fetchInvoicePdfBlob(
        docId,
        getData,
        buildInvoicePdfHints(rowData),
      );
      const filename = buildInvoicePdfFilename(rowData);

      if (action.type === 'preview') {
        closePdfPreview();
        const blobUrl = URL.createObjectURL(blob);
        pdfBlobRef.current = blob;
        const serie = rowData?.serie ?? rowData?.series ?? '';
        const numero = rowData?.numero ?? rowData?.number ?? '';
        setPdfPreview({
          isOpen: true,
          blobUrl,
          title: `Factura ${serie ? `${serie}-` : ''}${numero || docId}`,
          filename,
        });
        return;
      }

      if (action.type === 'download') {
        downloadPdfBlob(blob, filename);
        return;
      }

      openPdfBlobInNewTab(blob);
    } catch (error) {
      toast({
        title: 'Error al obtener el PDF',
        description: error?.message || 'No se pudo descargar la factura',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Cleanup del polling cuando el componente se desmonta
  useEffect(() => {
    return () => {
      stopSyncProgressPolling();
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      const progress = await checkSyncProgress();
      if (progress?.isRunning) {
        setShowSyncModal(true);
        startSyncProgressPolling();
      }
      await loadData({}, 1, true);
    };

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('🎬 SearchQuery useEffect triggered');
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortDir]);

  useEffect(() => {
    if (skipSortReloadRef.current) {
      skipSortReloadRef.current = false;
      return;
    }
    if (!isSyncing) {
      loadData(currentFilters, 1, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDir]);

  // Debug de estados
  useEffect(() => {
    console.log('🎭 States changed:', {
      isLoading,
      isSyncing,
      showSyncModal,
      syncProgress: {
        isRunning: syncProgress.isRunning,
        phase: syncProgress.phase,
        current: syncProgress.current,
        total: syncProgress.total
      }
    });
  }, [isLoading, isSyncing, showSyncModal, syncProgress]); 

  // Efecto para asegurar que el loader global esté desactivado durante sync
  useEffect(() => {
    if (showSyncModal || isSyncing) {
      console.log('🚫 Hiding global loader due to sync modal');
      loaderService.hideLoader();
      setIsLoading(false);
    }
  }, [showSyncModal, isSyncing]); 

  return (
    <AdminPageShell
      title="Facturas"
      showSearch={false}
      showDateFilter={false}
      actions={
        <>
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleManualSync}
            isLoading={isSyncing}
            loadingText="Sincronizando"
            mr={2}
          >
            Sincronizar facturas
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/admin/account-statement?type=receivable')}
          >
            Estado de cuenta clientes
          </Button>
        </>
      }
    >
      <SyncProgressModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        progress={syncProgress}
      />
      <InvoiceFilters
        onSearch={handleSearch}
        isLoading={isLoading && !showSyncModal}
      />
      <InvoicesTable
        columnsData={columnsDataInvoices}
        data={data}
        page={page - 1}
        setPage={(p) => handlePageChange(p + 1)}
        totalPages={totalPages}
        handlePrintInvoice={handlePrintInvoice}
        isLoading={isLoading && !showSyncModal}
        total={total}
        sorting={sorting}
        onSortingChange={setSorting}
      />

      <InvoicePdfPreviewModal
        isOpen={pdfPreview.isOpen}
        onClose={closePdfPreview}
        blobUrl={pdfPreview.blobUrl}
        title={pdfPreview.title}
        onDownload={() => {
          if (pdfBlobRef.current) {
            downloadPdfBlob(pdfBlobRef.current, pdfPreview.filename);
          }
        }}
      />
    </AdminPageShell>
  );
}
